import React from 'react';
import { motion } from 'framer-motion';

const CookiePolicy = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <motion.div
          className="legal-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Cookie Policy for PawPrints</h1>
          <p className="effective-date"><strong>Last Updated:</strong> August 2025</p>
          
          <p>This Cookie Policy explains how PawPrints ("we", "our", or "us") uses cookies and similar technologies when you visit our website. This policy helps you understand what cookies are, how we use them, and the choices you have regarding their use.</p>

          <section>
            <h2>1. What Are Cookies?</h2>
            <p>Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners about how users interact with their sites.</p>
            
            <p>Cookies can be "session cookies" (which are deleted when you close your browser) or "persistent cookies" (which remain on your device for a set period or until you delete them).</p>
          </section>

          <section>
            <h2>2. How We Currently Use Cookies</h2>
            <p>At present, PawPrints only uses strictly necessary cookies that are essential for the website to function properly. These cookies do not require your consent under privacy laws such as GDPR and CCPA.</p>
            
            <h3>Strictly Necessary Cookies</h3>
            <div className="cookie-table">
              <div className="cookie-row">
                <div className="cookie-cell"><strong>Cookie Type</strong></div>
                <div className="cookie-cell"><strong>Purpose</strong></div>
                <div className="cookie-cell"><strong>Duration</strong></div>
              </div>
              <div className="cookie-row">
                <div className="cookie-cell">Session Cookies</div>
                <div className="cookie-cell">Enable website navigation and maintain your browsing session</div>
                <div className="cookie-cell">Session only (deleted when browser closes)</div>
              </div>
              <div className="cookie-row">
                <div className="cookie-cell">Navigation State</div>
                <div className="cookie-cell">Remember your current page and smooth scrolling preferences</div>
                <div className="cookie-cell">Session only</div>
              </div>
              <div className="cookie-row">
                <div className="cookie-cell">Security Cookies</div>
                <div className="cookie-cell">Protect against cross-site request forgery attacks</div>
                <div className="cookie-cell">Session only</div>
              </div>
            </div>
          </section>

          <section>
            <h2>3. Cookies We May Use in the Future</h2>
            <p>As PawPrints evolves, we may introduce additional cookies to enhance your experience. When we do, we will update this policy and, where required by law, obtain your consent first.</p>
            
            <h3>Analytics Cookies (Not Currently Used)</h3>
            <p>We may use analytics services like Google Analytics to understand how visitors interact with our website. These cookies would help us:</p>
            <ul>
              <li>Understand which pages are most popular</li>
              <li>See how visitors navigate through the site</li>
              <li>Identify technical issues and improve site performance</li>
              <li>Make data-driven decisions about website improvements</li>
            </ul>
            
            <h3>Performance Cookies (Not Currently Used)</h3>
            <p>Performance cookies may be used to:</p>
            <ul>
              <li>Monitor website loading times</li>
              <li>Track error rates and technical issues</li>
              <li>Optimize content delivery</li>
            </ul>
            
            <h3>User Account Cookies (Not Currently Used)</h3>
            <p>If we introduce user accounts, we may use cookies to:</p>
            <ul>
              <li>Keep you logged in between sessions</li>
              <li>Remember your preferences and settings</li>
              <li>Provide personalized experiences</li>
              <li>Maintain shopping cart contents</li>
            </ul>
            
            <h3>Marketing Cookies (Not Currently Used)</h3>
            <p>We do not currently use marketing cookies, but if introduced in the future, they might:</p>
            <ul>
              <li>Track visits across websites for advertising purposes</li>
              <li>Provide relevant advertisements</li>
              <li>Measure advertising campaign effectiveness</li>
            </ul>
          </section>

          <section>
            <h2>4. Third-Party Cookies</h2>
            <p>Currently, we do not use any third-party cookies. However, if we integrate with external services in the future (such as analytics tools, social media widgets, or payment processors), those services may place their own cookies on your device.</p>
            
            <p>Common third-party services that may be integrated in the future include:</p>
            <ul>
              <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
              <li><strong>Social Media Platforms:</strong> For social sharing buttons or embedded content</li>
              <li><strong>Payment Processors:</strong> For handling app store purchases or subscriptions</li>
              <li><strong>Customer Support Tools:</strong> For chat widgets or help desk functionality</li>
            </ul>
          </section>

          <section>
            <h2>5. Your Cookie Choices</h2>
            
            <h3>Browser Settings</h3>
            <p>You can control cookies through your browser settings. Most browsers allow you to:</p>
            <ul>
              <li>View what cookies have been set and delete them individually</li>
              <li>Block third-party cookies</li>
              <li>Block all cookies from specific sites</li>
              <li>Block all cookies from being set</li>
              <li>Delete all cookies when you close your browser</li>
            </ul>
            
            <h3>Impact of Disabling Cookies</h3>
            <p>Please note that disabling cookies may affect your experience on our website:</p>
            <ul>
              <li><strong>Strictly necessary cookies:</strong> Disabling these may prevent the website from functioning properly</li>
              <li><strong>Analytics cookies:</strong> Disabling these won't affect your experience but will prevent us from improving the site</li>
              <li><strong>User account cookies:</strong> Disabling these may require you to log in repeatedly</li>
            </ul>
            
            <h3>Future Cookie Consent</h3>
            <p>When we introduce non-essential cookies, we will:</p>
            <ul>
              <li>Display a clear cookie consent banner</li>
              <li>Provide granular options to accept or reject different types of cookies</li>
              <li>Offer an easy way to change your preferences at any time</li>
              <li>Respect your choices and only set cookies you've consented to</li>
            </ul>
          </section>

          <section>
            <h2>6. Legal Basis for Cookie Processing</h2>
            
            <h3>Strictly Necessary Cookies</h3>
            <p>We process strictly necessary cookies based on our legitimate interest in providing you with a functional website. These cookies are essential for the website to work and cannot be disabled.</p>
            
            <h3>Other Cookies (When Introduced)</h3>
            <p>For any non-essential cookies we may introduce in the future:</p>
            <ul>
              <li><strong>EU/EEA visitors:</strong> We will obtain your explicit consent before setting these cookies</li>
              <li><strong>California residents:</strong> We will provide clear information and an easy opt-out mechanism</li>
              <li><strong>Other jurisdictions:</strong> We will comply with applicable local privacy laws</li>
            </ul>
          </section>

          <section>
            <h2>7. Data Retention</h2>
            <p>Currently, all cookies we use are session cookies that are automatically deleted when you close your browser. In the future, if we use persistent cookies, we will:</p>
            <ul>
              <li>Set appropriate expiration dates based on the cookie's purpose</li>
              <li>Regularly review and delete outdated cookie data</li>
              <li>Allow you to delete cookies manually through your browser or our preference center</li>
            </ul>
          </section>

          <section>
            <h2>8. International Data Transfers</h2>
            <p>Currently, our website is hosted in secure data centers and cookie data remains within appropriate jurisdictions. If we partner with international service providers in the future, we will ensure adequate protection for any personal data processed through cookies.</p>
          </section>

          <section>
            <h2>9. Updates to This Cookie Policy</h2>
            <p>We will update this Cookie Policy when:</p>
            <ul>
              <li>We introduce new types of cookies</li>
              <li>We change how we use existing cookies</li>
              <li>Legal requirements change</li>
              <li>We integrate with new third-party services</li>
            </ul>
            
            <p>We will notify you of significant changes by:</p>
            <ul>
              <li>Updating the "Last Updated" date at the top of this policy</li>
              <li>Displaying a notification on our website</li>
              <li>Requesting new consent where required by law</li>
            </ul>
          </section>

          <section>
            <h2>10. Contact Us</h2>
            <p>If you have questions about our use of cookies or this Cookie Policy, please contact us:</p>
            <p><strong>📧 Email:</strong> <a href="mailto:support@pawprintsapp.com">support@pawprintsapp.com</a></p>
            <p><strong>🐾 App:</strong> PawPrints</p>
            
            <p>For questions about your privacy rights or to exercise your rights regarding cookies and personal data, please refer to our <a href="/privacy">Privacy Policy</a> or contact us using the information above.</p>
          </section>

          <div className="cookie-status">
            <h3>Current Cookie Status</h3>
            <p><strong>✅ Strictly Necessary:</strong> Active (required for website function)</p>
            <p><strong>❌ Analytics:</strong> Not in use</p>
            <p><strong>❌ Marketing:</strong> Not in use</p>
            <p><strong>❌ Third-party:</strong> Not in use</p>
            <p><em>This status will be updated as new features are added to PawPrints.</em></p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;